"use client";

import { useEffect, useState } from "react";
import { DropdownMenuItem } from "@/app/_module/components/ui/dropdown-menu";

// Bulk lead-source and tag menu items, shared by the dialer's Lead Queue and
// the Lite selection widget so the two menus can never drift apart.

// Bulk source options — fetches available lead sources from API
export function BulkSourceOptions({ selectedLeads }: { selectedLeads: any[] }) {
  const [sources, setSources] = useState<string[]>([]);
  useEffect(() => {
    (async () => {
      try {
        const { default: srcApi } = await import("@/app/_module/services/api");
        const { getApiUrl } = await import("@/app/_module/services/config");
        const res = await srcApi.get<any>({ url: getApiUrl("/leads/list-names"), auth: true });
        setSources(res?.data?.data || res?.data || []);
      } catch {}
    })();
  }, []);

  const handleSource = async (listName: string) => {
    try {
      const { default: srcApi } = await import("@/app/_module/services/api");
      const { getApiUrl } = await import("@/app/_module/services/config");
      const { showToast } = await import("@/app/_module/utils/show-toast");
      await srcApi.post({
        url: getApiUrl("/leads/bulk-update-source"),
        body: { leadIds: selectedLeads.map(l => l.id), listName },
        auth: true,
      });
      showToast(`Set ${selectedLeads.length} leads to source "${listName}"`, "success");
    } catch {}
  };

  if (sources.length === 0) return null;

  return (
    <>
      {sources.map((source) => (
        <DropdownMenuItem
          key={source}
          onClick={() => handleSource(source)}
          className="cursor-pointer gap-2"
        >
          <span className="text-sm truncate">{source}</span>
        </DropdownMenuItem>
      ))}
    </>
  );
}

// Bulk tag options — fetches available tags from API (presets + custom)
export function BulkTagOptions({ selectedLeads }: { selectedLeads: any[] }) {
  const [tags, setTags] = useState<string[]>([]);
  useEffect(() => {
    (async () => {
      try {
        const { default: tagApi } = await import("@/app/_module/services/api");
        const { getApiUrl } = await import("@/app/_module/services/config");
        const res = await tagApi.get<any>({ url: getApiUrl("/leads/tags"), auth: true });
        const dbTags: string[] = res?.data?.data || res?.data || [];
        // Merge presets + DB tags
        const presets = ["hot_lead", "interested", "callback", "not_home", "referral", "follow_up", "spanish"];
        const all = [...new Set([...presets, ...dbTags])];
        setTags(all);
      } catch {
        setTags(["hot_lead", "interested", "callback", "not_home", "referral", "follow_up", "spanish"]);
      }
    })();
  }, []);

  const handleTag = async (tag: string) => {
    try {
      const { default: bulkApi } = await import("@/app/_module/services/api");
      const { getApiUrl } = await import("@/app/_module/services/config");
      const { showToast } = await import("@/app/_module/utils/show-toast");
      const leadIds = selectedLeads.map(l => l.id);
      await bulkApi.post({
        url: getApiUrl("/leads/bulk-add-tag"),
        body: { leadIds, tag },
        auth: true,
      });
      showToast(`Tagged ${selectedLeads.length} leads with "${tag.replace(/_/g, " ")}"`, "success");
    } catch {}
  };

  return (
    <>
      {tags.map((tag) => (
        <DropdownMenuItem
          key={tag}
          onClick={() => handleTag(tag)}
          className="cursor-pointer gap-2"
        >
          <span className="text-sm">{tag.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase())}</span>
        </DropdownMenuItem>
      ))}
    </>
  );
}
